import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import PageContainer from '../../../components/Redesing/page-container';
import PageContent from '../../../components/Redesing/page-content';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import EditIcon from '@mui/icons-material/Edit';
import { changeTab, history } from '../../../utils';
import DeleteIcon from '@mui/icons-material/Delete';
import useMembershipImages from '../../../hooks/membership/query/useMembershipImages';
import { IImage } from '../../../ts/interface/image.interface';
import { useParams } from 'react-router';
import { Button, ButtonGroup, Stack } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import CardView from '../../../components/Redesing/card-view';
import MembershipPerks from '../assets/membership-perks';
import MembershipLocationAccess from '../assets/membership-location-access';
import MembershipPricing from '../membershipPricing';
import MembershipDocumentsList from '../assets/membership-documents-list';
import UserMembership from '../assets/user-membership';
import { useMembershipsRelayQuery } from '../../../hooks/membership/query/V2/useMembershipsRelay';
import { mapMembershipsToLegacy } from '../../../hooks/membership/query/V2/membershipMappers';
import { IMembership } from '../../../ts/interface/membership.interface';

import MembershipCardDetails from '../assets/general-card-details';
import { ISystem } from '../../../ts/interface/system.interface';
import PerksModal from '../PerksModal';
import MovaticCustomModal from '../../../components/Modal/MovaticCustomModal';
import { archiveMembership } from '../../../api/memberships';
import { useSelector } from 'react-redux';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ReplaceMembershipModal from '../assets/replace-membership-modal';
import ArchiveRidersWarning from '../assets/archive-riders-warning';
import { canReplaceMembership } from '../membership-permissions';

function OrganizationMembershipDetails() {
  const { membershipId, currentTab: currentActiveTab } = useParams<{
    membershipId: string;
    currentTab: string;
  }>();
  const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
  const [showReplaceModal, setShowReplaceModal] = useState<boolean>(false);
  const [membersRefreshKey, setMembersRefreshKey] = useState(0);
  const isMovatic = canReplaceMembership(
    useSelector(
      (state: { admin: { admin: { admin: { movaticAccess?: number } } } }) =>
        state.admin?.admin?.admin?.movaticAccess
    )
  );
  const [currentTabV, setCurrentTabV] = useState('general');
  const [showPerksModal, setShowPerksModal] = useState(false);
  const [selectedPerkData, setSelectedPerkData] = useState({
    perkID: '',
    stationID: '',
  });

  useEffect(() => {
    if (currentActiveTab !== currentTabV) {
      setCurrentTabV(currentActiveTab);
    }
  }, [currentActiveTab, currentTabV]);

  const {
    data: membershipData,
    isLoading: isLoadingMembership,
    refetch: refetchMembershipData,
  } = useMembershipsRelayQuery(
    {
      membershipId,
    },
    {
      enabled: Boolean(membershipId),
    }
  );

  // Map GraphQL membership to legacy format (single membership)
  const membership = useMemo((): IMembership | undefined => {
    if (!membershipData || membershipData.length === 0) return undefined;
    const [mapped] = mapMembershipsToLegacy(membershipData);
    return mapped;
  }, [membershipData]);
  const rawMembership = membershipData?.[0];
  const isCurrentMembership = rawMembership?.id === membershipId;

  // Plans this one replaced point at it (old -> new) and carry an archive
  // date, so B can say "Replaces A". A live plan pointing here is a legacy
  // new -> old pointer (B on A's page), not a plan this one replaced.
  const { data: replacedPlans } = useMembershipsRelayQuery(
    {
      replacementMembershipOldId: rawMembership?.oldId ?? undefined,
      hasArchiveDate: true,
    },
    { enabled: Boolean(rawMembership?.oldId), limit: 20 }
  );
  const replacesNames = useMemo(
    () => (replacedPlans ?? []).map((plan) => plan.name),
    [replacedPlans]
  );

  // The organization view has no selected system, so it must not hit the
  // system-scoped /currentsystem endpoint. The membership's own system is
  // already included in the GraphQL payload and is all this page needs.
  const currentSystem = (membership?.system ?? {}) as ISystem;

  const { data: membershipImagesData, isLoading: isLoadingMembershipImages } =
    useMembershipImages({ enabled: false });
  const membershipImages = useMemo(
    () => (membershipImagesData as IImage[]) || [],
    [membershipImagesData]
  );

  const currentLogo = useMemo(() => {
    return membershipImages?.find(
      (image: IImage) =>
        image.id === membership?.logo_image_id &&
        (image.type === 'MembershipLogo' || image.type === 'SystemLogo')
    );
  }, [membershipImages, membership]);

  const getMembershipIcon = useMemo(() => {
    const logo = currentLogo;
    const imageStyle = {
      height: '45px',
      maxWidth: '45px',
    };
    return logo?.image ? (
      <img
        alt=""
        src={`data:image/png;base64,${logo.image}`}
        style={{ ...imageStyle, borderRadius: '50%', objectFit: 'cover' }}
      />
    ) : (
      <CardMembershipIcon />
    );
  }, [currentLogo]);

  const handleTabsChange = (event: SyntheticEvent<Element, Event>, value: string) => {
    changeTab(
      `${membershipId}/${value}`,
      `${membershipId}/${currentTabV}`,
      'memberships'
    );
  };

  const topActionButtons = () => {
    if (membership?.archived_on && new Date(membership.archived_on) <= new Date()) {
      return null;
    }
    return [
      {
        startIcon: <EditIcon />,
        label: 'Edit',
        onClick: () => history.push(`/memberships/edit/${membership?.id}`),
      },
      {
        startIcon: <DeleteIcon />,
        label: 'Archive',
        onClick: () => setShowArchiveModal(!showArchiveModal),
      },
      ...(isMovatic
        ? [
            {
              startIcon: <SwapHorizIcon />,
              label: 'Replace with another plan…',
              disabled: !isCurrentMembership,
              onClick: () => {
                if (isCurrentMembership) setShowReplaceModal(true);
              },
            },
          ]
        : []),
    ];
  };

  function handlePerksCloseModal() {
    setShowPerksModal(false);
    setSelectedPerkData({
      perkID: '',
      stationID: '',
    });
  }

  return (
    <PageContainer>
      <PageContent
        backLink={'/memberships'}
        avatarColor={currentLogo?.image && 'none'}
        contentTitle={'Memberships'}
        itemName={membership?.name}
        itemChipValue={membership?.id}
        chipLabel={'Membership ID'}
        detailIcon={getMembershipIcon}
        currentTabV={currentTabV}
        itemTopButtons={<IconButtonMenu buttons={topActionButtons() || []} />}
        handleTabsChange={handleTabsChange}
        tabsList={[
          { label: 'General', value: 'general' },
          { label: 'Members', value: 'members' },
        ]}
        tabsChildren={
          <>
            {currentTabV === 'general' && (
              <Grid container spacing={2}>
                <Grid xs={12} lg={4}>
                  <Stack spacing={2}>
                    <CardView
                      isLoading={isLoadingMembership || isLoadingMembershipImages}
                      content={
                        <MembershipCardDetails
                          membership={membership as IMembership}
                          membershipImages={membershipImages || []}
                          currentSystem={currentSystem}
                          showCurrentSystem={true}
                          replacesNames={replacesNames}
                        />
                      }
                      title={'Membership Details'}
                    />
                  </Stack>
                </Grid>
                <Grid xs={12} lg={8}>
                  <Stack spacing={2}>
                    <>
                      <MembershipPerks
                        perks={
                          membership?.perks.filter((perk: any) => perk.system_id) || []
                        }
                        isLoading={isLoadingMembership}
                      />
                      <MembershipLocationAccess
                        onAddClick={() => setShowPerksModal(!showPerksModal)}
                        locations={
                          membership?.perks.filter(
                            (perk: any) => !perk.system_id && perk.archived_on === null
                          ) || []
                        }
                        isLoading={isLoadingMembership}
                      />
                      <MembershipPricing prices={membership?.prices || []} />
                      {membership?.auth_type === 'admin' && (
                        <MembershipDocumentsList docs={membership?.data?.docs || []} />
                      )}
                    </>
                  </Stack>
                </Grid>
              </Grid>
            )}
            {currentTabV === 'members' && (
              <UserMembership
                key={`${membershipId}:${membersRefreshKey}`}
                authType={membership?.auth_type || ''}
                membershipId={membershipId}
                systemId={currentSystem.gid}
              />
            )}
          </>
        }
      />
      <MovaticCustomModal
        open={showArchiveModal}
        onClose={() => setShowArchiveModal(!showArchiveModal)}
        title="Archive Membership"
        content={
          <>
            Are you sure you would like to archive this membership? This cannot be undone.
            <ArchiveRidersWarning
              membershipId={membership?.id}
              enabled={isMovatic && showArchiveModal && isCurrentMembership}
              onReplaceInstead={() => {
                if (!isCurrentMembership) return;
                setShowArchiveModal(false);
                setShowReplaceModal(true);
              }}
            />
          </>
        }
        action={
          <ButtonGroup aria-label="outlined primary button group">
            <Button
              variant="contained"
              onClick={() => {
                archiveMembership(membership?.id, currentSystem.id).then(() => {
                  history.push('/memberships');
                });
                setShowArchiveModal(!showArchiveModal);
              }}
            >
              Submit
            </Button>
            <Button
              color={'secondary'}
              variant="contained"
              onClick={() => setShowArchiveModal(!showArchiveModal)}
            >
              Cancel
            </Button>
          </ButtonGroup>
        }
        customStyles={undefined}
        useContentDividers={undefined}
      />

      {isMovatic && rawMembership && (
        <ReplaceMembershipModal
          open={showReplaceModal}
          onClose={() => setShowReplaceModal(false)}
          membership={rawMembership}
          routeMembershipId={membershipId}
          onReplaced={() => {
            refetchMembershipData();
            // Refetch from page one, discarding accumulated member rows and cursors.
            setMembersRefreshKey((key) => key + 1);
          }}
        />
      )}

      {showPerksModal && membership && (
        <PerksModal
          openModal={showPerksModal}
          systemID={currentSystem?.gid || ''}
          locationIDs={membership.perks.map((perk) => perk.station_id)}
          membership={membership}
          perkData={selectedPerkData}
          refetchMembershipData={refetchMembershipData}
          handleCloseModal={handlePerksCloseModal}
        />
      )}
    </PageContainer>
  );
}

export default OrganizationMembershipDetails;
